import React from 'react';
import { Modal, Form, Input, message, Row, Col } from 'antd';
import { useCreateWarehouseMutation, useUpdateWarehouseMutation, Warehouse } from '../../store/api/referencesApi';
import AddressAutocomplete from './AddressAutocomplete';

// TextArea not used here

export interface CreateWarehouseFormData {
  name: string;
  city: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

interface CreateWarehouseModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (warehouse: Warehouse) => void;
  warehouse?: Warehouse | null; // Для режима редактирования
  title?: string;
}

const CreateWarehouseModal: React.FC<CreateWarehouseModalProps> = ({
  isOpen,
  onCancel,
  onSuccess,
  warehouse = null,
  title = 'Добавить склад'
}) => {
  const [form] = Form.useForm();
  const [createWarehouse, { isLoading: isCreating }] = useCreateWarehouseMutation();
  const [updateWarehouse, { isLoading: isUpdating }] = useUpdateWarehouseMutation();

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!warehouse;

  const handleSubmit = async (values: CreateWarehouseFormData) => {
    try {
      console.log('🏢 Creating/Updating warehouse:', values);
      
      if (isEditMode && warehouse) {
        const updatedWarehouse = await updateWarehouse({
          id: warehouse.id,
          data: values
        }).unwrap();
        
        console.log('✅ Warehouse updated successfully:', updatedWarehouse);
        message.success('Склад успешно обновлен');
        onSuccess(updatedWarehouse);
      } else {
        const newWarehouse = await createWarehouse(values).unwrap();
        
        console.log('✅ Warehouse created successfully:', newWarehouse);
        message.success('Склад успешно создан');
        onSuccess(newWarehouse);
      }
      
      form.resetFields();
      onCancel();
    } catch (error: any) {
      console.error('❌ Error creating/updating warehouse:', error);
      const errorMessage = error?.data?.error || 'Ошибка при создании/обновлении склада';
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Заполняем форму данными для редактирования
  React.useEffect(() => {
    if (isOpen && warehouse) {
      form.setFieldsValue({
        name: warehouse.name,
        city: warehouse.city,
        address: warehouse.address,
        contactPerson: warehouse.contactPerson,
        phone: warehouse.phone
      });
    } else if (isOpen && !warehouse) {
      form.resetFields();
    }
  }, [isOpen, warehouse, form]);

  return (
    <Modal
      title={isEditMode ? 'Редактировать склад' : title}
      open={isOpen}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText={isEditMode ? 'Обновить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={isLoading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Название склада"
          name="name"
          rules={[
            { required: true, message: 'Введите название склада' },
            { min: 2, message: 'Название должно содержать минимум 2 символа' }
          ]}
        >
          <Input placeholder="Например: Склад_СПБ" maxLength={255} />
        </Form.Item>

        <Form.Item
          label="Город"
          name="city"
          rules={[
            { required: true, message: 'Введите город' },
            { min: 2, message: 'Город должен содержать минимум 2 символа' }
          ]}
        >
          <Input placeholder="Например: Санкт-Петербург" maxLength={100} />
        </Form.Item>

        <Form.Item
          label="Адрес"
          name="address"
        >
          <AddressAutocomplete placeholder="Полный адрес склада" style={{ width: '100%' }} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Контактное лицо"
              name="contactPerson"
            >
              <Input 
                placeholder="ФИО контактного лица"
                maxLength={255}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Телефон"
              name="phone"
            >
              <Input 
                placeholder="+7 (xxx) xxx-xx-xx"
                maxLength={50}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateWarehouseModal; 